import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import UserPrefsManager from '../components/user_prefs';
import PasswordResetDialog from '../components/password_reset';
import MFASetupDialog from '../components/mfa_setup';
// import MFABackupCodesDialog from '../components/mfa_regen';
import DeactivateMFADialog from '../components/mfa_prompt';
import { formatDateTime } from '../components/enums';
import { isValidEmail, isValidName, isValidTimeZone } from '../components/validation';
import Toast from '../components/toast';
import { TIME_ZONES } from '../components/time_zones';

// Material UI
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

const DataSwitch = styled(Switch)(({ theme }) => ({
	width: 30,
	height: 18,
	padding: 0,
	display: 'flex',
	'& .MuiSwitch-switchBase': {
		padding: 2,
		'&.Mui-checked': {
			transform: 'translateX(14px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				opacity: 1,
				backgroundColor: '#B1282E',
			},
		},
	},
	'& .MuiSwitch-thumb': {
		boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
		width: 12,
		height: 14,
		borderRadius: 2,
		transition: theme.transitions.create(['width'], {
			duration: 200,
		}),
	},
	'& .MuiSwitch-track': {
		borderRadius: 4,
		opacity: 1,
		backgroundColor: '#0F1820',
		boxSizing: 'border-box',
	},
}));

// System Trend sub-page rendering (Location Name, Title)
export default function UserProfile() {
	const [ searchParams ] = useSearchParams();

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});

	const [userInfo, setUserInfo] = useState({
		id: 0,
		email: '',
		password_reset: false,
		first_name: '',
		last_name: '',
		job_title: '',
		company: '',
		time_zone: '',
		metric: false,
		date_joined: '1990-01-01T00:00:00.000000Z',
		last_login: '1990-01-01T00:00:00.000000Z',
		pass_last_reset: '1990-01-01T00:00:00.000000Z',
		is_active: false,
		is_superuser: false,
		is_staff: false
	});
	
	// Input fields for the form
	const [txtFirstName, setTxtFirstName] = useState('');
	const [txtLastName, setTxtLastName] = useState('');
	const [txtJobTitle, setTxtJobTitle] = useState('');
	const [txtCompany, setTxtCompany] = useState('');
	const [txtEmail, setTxtEmail] = useState('');
	const [txtTimeZone, setTxtTimeZone] = useState('');
	const [chkMetric, setChkMetric] = useState(false);
	
	// Keep track of changes to the form for saving purposes
	const [isChanged, setIsChanged] = useState(false);

	// Password reset dialog open state
	const [passDialogOpen, setPassDialogOpen] = useState(false);

	// MFA status and dialog open states
	const [mfaStatusLoaded, setMFAStatusLoaded] = useState(false);
	const [mfaActive, setMFAActive] = useState(false);
	const [mfaSetupDialogOpen, setMFASetupDialogOpen] = useState(false);
	const [mfaDeactivateDialogOpen, setMFADeactivateDialogOpen] = useState(false);
	// const [mfaBackupDialogOpen, setMFABackupDialogOpen] = useState(false);
	
	// Form Validity
	const [emailValid, setEmailValid] = useState(false);
	const [firstNameValid, setFirstNameValid] = useState(false);
	const [lastNameValid, setLastNameValid] = useState(false);
	const [timeZoneValid, setTimeZoneValid] = useState(false);

	// State for the current password dialog
	const [currentPasswordDialogOpen, setCurrentPasswordDialogOpen] = useState(false);
	const [currentPassword, setCurrentPassword] = useState('');

	// Form Validity checks
	useEffect(() => { setEmailValid(isValidEmail(txtEmail)); }, [txtEmail]);
	
	useEffect(() => { setFirstNameValid(isValidName(txtFirstName)); }, [txtFirstName]);
	
	useEffect(() => { setLastNameValid(isValidName(txtLastName)); }, [txtLastName]);
	
	useEffect(() => { setTimeZoneValid(isValidTimeZone(txtTimeZone)); }, [txtTimeZone]);

	// Verify all form requirements are met
	const verifyRequirementsMet = emailValid && firstNameValid && lastNameValid && timeZoneValid;

	// Get the user info from the API
	useEffect(() => {
		getUserInfo();
		getUserMFAStatus();

		// Check for passreset query parameter
		if (searchParams.has('passreset')) {
			setPassDialogOpen(true);
		}
	}, [searchParams]);

	// Check the MFA activation status
	useEffect(() => {
		// If either dialog(s) open state changes and is false (they were closed), update the MFA status
		if (!mfaSetupDialogOpen || !mfaDeactivateDialogOpen) {
			getUserMFAStatus();
		}
	}, [mfaSetupDialogOpen, mfaDeactivateDialogOpen]);
	
	// Keep track of the change in form data
	useEffect(() => {
		if(userInfo.first_name !== txtFirstName ||
			userInfo.last_name !== txtLastName ||
			userInfo.job_title !== txtJobTitle ||
			userInfo.company !== txtCompany ||
			userInfo.email !== txtEmail ||
			userInfo.time_zone !== txtTimeZone ||
			userInfo.metric !== chkMetric) {
			setIsChanged(true);
		} else {
			setIsChanged(false);
		}
	}, [userInfo, txtFirstName, txtLastName, txtJobTitle, txtCompany, txtEmail, txtTimeZone, chkMetric]);

	// Get the user's profile info
	function getUserInfo() {
		axiosInstance.get('/users/').then(response => {
			if (response.data.length === 0) { return; }

			setUserInfo(response.data);

			setTxtEmail(response.data.email);
			setTxtFirstName(response.data.first_name);
			setTxtLastName(response.data.last_name);
			setTxtJobTitle(response.data.job_title);
			setTxtCompany(response.data.company);
			setTxtTimeZone(response.data.time_zone);
			setChkMetric(response.data.metric);
		});
	}

	// Get the user's mfa status
	function getUserMFAStatus() {
		axiosInstance.get('/auth/mfa/user-active-methods/')
			.then(response => {
				const mfaMethods = response.data.results;
				if (Array.isArray(mfaMethods) && mfaMethods.length > 0) {
					setMFAActive(mfaMethods.some(method => method.name === 'app'));
				} else {
					setMFAActive(false);
				}
				setMFAStatusLoaded(true);
			})
			.catch(error => {
				console.log('Error getting MFA status:', error);
				setMFAStatusLoaded(false);
			});
	}
	
	// Update the user's profile info
	function updateUserInfo(password = '', onSuccess = () => {}, onError = () => {}) {
		// Prepare the payload for the API request
		const payload = {
			auth: {
				current_password: password
			},
			data: {
				first_name: txtFirstName === '' ? userInfo.first_name : txtFirstName,
				last_name: txtLastName === '' ? userInfo.last_name : txtLastName,
				job_title: txtJobTitle,
				company: txtCompany,
				email: !emailValid ? userInfo.email : txtEmail,
				time_zone: !timeZoneValid ? userInfo.time_zone : txtTimeZone,
				metric: chkMetric
			}
		};

		// Call the User Update endpoint to update the database
		axiosInstance.put(`/users/change/${userInfo.id}/`, payload)
			.then(response => {
				setToast({ severity: 'success', message: 'User info updated successfully.', open: true });
				getUserInfo(); // Refresh user data
				setIsChanged(false); // Clear the save changes bar
				onSuccess(); // Success callback
			})
			.catch(error => {
				if (error.response) {
					const { status, data } = error.response;

					// Check for specific error codes and messages
					if (status === 403 && data.error === 'Current password is incorrect.') {
						setToast({ severity: 'error', message: data.error, open: true });
					} else if (status === 400 && data.error === 'Current password is required to update the email address.') {
						setToast({ severity: 'error', message: data.error, open: true });
					} else {
						setToast({ severity: 'error', message: 'Error updating user info. Please try again.', open: true });
					}
				} else {
					// Handle other errors (e.g., network issues)
					setToast({ severity: 'error', message: 'An unexpected error occurred. Please try again.', open: true });
				}
				onError(); // Error callback
			});

		// Save the user's timezone and measurement units preferences in local storage
		UserPrefsManager.set('userTimeZone', (!timeZoneValid ? userInfo.time_zone : txtTimeZone));
		UserPrefsManager.set('userMeasUnits', (chkMetric ? 'metric' : 'imperial'));
	}

	// Handle form changes save
	function handleSave() {
		if (txtEmail !== userInfo.email) {
			// Open the current password dialog if the email is being updated
			setCurrentPasswordDialogOpen(true);
		} else {
			// Proceed with saving if the email is not being updated
			updateUserInfo();
		}
	}

	// Function to handle saving after entering the current password
	function handleSaveWithPassword() {
		updateUserInfo(
			currentPassword,
			() => {
				// On success, close the dialog
				setCurrentPassword('');
				setCurrentPasswordDialogOpen(false);
			},
			() => {
				// On error, keep the dialog open
				setCurrentPassword(''); // Optionally clear the password field
			}
		);
	}

	// Handle form changes reset
	function handleReset() {
		setTxtFirstName(userInfo.first_name);
		setTxtLastName(userInfo.last_name);
		setTxtJobTitle(userInfo.job_title);
		setTxtCompany(userInfo.company);
		setTxtEmail(userInfo.email);
		setTxtTimeZone(userInfo.time_zone);
		setChkMetric(userInfo.metric);

		setIsChanged(false);
	}

	// Handle password reset dialog open
	function handlePassResetOpen() {
		setPassDialogOpen(true);
	}
	
	// Handle MFA setup dialog open
	function handleMFAStatusChange() {
		if (!mfaActive) {
			setMFASetupDialogOpen(true);
		} else {
			setMFADeactivateDialogOpen(true);
		}
	}

	// Handle MFA backup codes dialog open
	// function handleMFABackupOpen() {
	// 	setMFABackupDialogOpen(true);
	// }

	// User Avatar (First Name, Last Name, Job Title, Company, Color)
	function UserAvatar({ firstName, lastName, jobTitle, company, color = 'primary' }) {
		const initials = `${(firstName[0] || '')}${(lastName[0] || '')}`.toUpperCase();

		return (
			<Box width={200} height={300}>
				<Paper style={{ padding: 16, border: '1px solid lightgray', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
					<Avatar style={{ width: 100, height: 100, backgroundColor: color }}>
						<Typography fontFamily='Saira, sans-serif' variant="h2" style={{ fontWeight: 600 }}>{initials}</Typography>
					</Avatar>
					<Typography fontFamily='Saira, sans-serif' fontWeight={600} variant="h5" align="center" sx={{ pt: 1, pb: 0.5 }}>{firstName} {lastName}</Typography>
					<Typography fontFamily='Saira, sans-serif' variant="subtitle2" align="center" sx={{ py: 0.5 }}>{jobTitle}</Typography>
					<Typography fontFamily='Saira, sans-serif' fontWeight={600} variant="subtitle2" align="center">{company}</Typography>
				</Paper>
			</Box>
		);
	}
	
	// User Account Activity (Account Creation, Last Login, Last Password Change)
	function AccountActivity() {

		const accountCreation = formatDateTime(userInfo.date_joined);
		const lastLogin = formatDateTime(userInfo.last_login);
		const lastPasswordChange = formatDateTime(userInfo.pass_last_reset);

		return (
			<Paper style={{ padding: 16, marginBottom: 16, border: '1px solid lightgray' }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Grid container spacing={3}>
						<Grid item xs={12}><Typography variant="h5">Account Activity</Typography></Grid>
						<Grid item xs={4} sm={3} lg={2}>
							<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>Account Created:</Typography>
							</Box>
						</Grid>
						<Grid item xs={8} sm={9} lg={10}>
						<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>{accountCreation}</Typography>
							</Box>
						</Grid>
						<Grid item xs={4} sm={3} lg={2}>
							<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>Last Login:</Typography>
							</Box>
						</Grid>
						<Grid item xs={8} sm={9} lg={10}>
						<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>{lastLogin}</Typography>
							</Box>
						</Grid>
						<Grid item xs={4} sm={3} lg={2}>
							<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>Password Last Changed:</Typography>
							</Box>
						</Grid>
						<Grid item xs={8} sm={9} lg={10}>
						<Box display="flex" alignItems="center" height="100%">
								<Typography fontFamily='Saira, sans-serif'>{lastPasswordChange}</Typography>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Paper>
		);
	}

	// Save Section with Reset and Save buttons
	function SaveSection() {
		return (
			isChanged && verifyRequirementsMet && (
				<Paper style={{ padding: 16, marginBottom: 16, border: '1px solid lightgray' }}>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="body1">You have unsaved changes. Would you like to save?</Typography>
						<Box>
							<Button variant="contained" onClick={handleReset} style={{ marginRight: 16 }}>Reset</Button>
							<Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
						</Box>
					</Box>
				</Paper>
			)
		);
	}

	const mfaSection = (
		<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap'}}>
			{ !mfaStatusLoaded ? (
				<Box sx={{ margin: 1 }}>
					<Typography fontFamily='Saira, sans-serif' fontWeight={600}>Loading...</Typography>
				</Box>
			) : (
				<>
					<Box sx={{ margin: 1 }}>
						<Typography fontFamily='Saira, sans-serif' fontWeight={600}>{mfaActive ? 'Active' : 'Inactive'}</Typography>
					</Box>
					<Box sx={{ margin: 1 }}>
						<Tooltip title={ (mfaActive ? 'Deactivate' : 'Setup') + ' multifactor authentication' }>
							<Button onClick={() => handleMFAStatusChange()} variant="contained" color="primary" >
								{mfaActive ? 'Deactivate' : 'Setup'}
							</Button>
						</Tooltip>
					</Box>
					{/* {mfaActive && (
						<Box sx={{ margin: 1 }}>
							<Tooltip title='Generate new backup codes'>
								<Button onClick={() => setMFABackupDialogOpen(true)} variant="contained" color="primary">Backup Codes</Button>
							</Tooltip>
						</Box>
					)} */}
				</>
			) } 
		</Box>
	);

	return (
		<>
			{/* Current Password Dialog */}
			<Dialog
				open={currentPasswordDialogOpen}
				onClose={() => setCurrentPasswordDialogOpen(false)}
				sx={{
					'.MuiDialog-paper': {
						border: '4px solid #B1282E',
						borderRadius: 2
					}
				}}
			>
				<DialogTitle backgroundColor='lightgray'>Confirm Email Change</DialogTitle>
				<DialogContent sx={{ marginTop: 2 }}>
					<Typography variant="body1" gutterBottom>
						To update your email address, please enter your current password.
					</Typography>
					<TextField
						autoFocus
						id="current_password"
						label="Current Password"
						type="password"
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
						inputProps={{ maxLength: 32 }}
						fullWidth
						margin="dense"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCurrentPasswordDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleSaveWithPassword} color="primary" variant="contained">Save</Button>
				</DialogActions>
			</Dialog>

			{/* Existing JSX */}
			<Paper
				padding='true'
				sx={{
					overflow: 'auto',
					boxShadow: '0',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					paddingX: '15px',
				}}
			>
				<Typography
					variant='h5'
					fontFamily='Saira, sans-serif'
					fontWeight='600'
					color='#0F1820'
					align='center'
					paddingTop='5px'
					paddingBottom={ userInfo.is_superuser || userInfo.is_staff ? '0px' : '15px'}
				>
					Account Settings
				</Typography>
				{ ( userInfo.is_superuser || userInfo.is_staff ) &&
					<Typography
						fontFamily='Saira, sans-serif'
						fontWeight='400'
						color='#0F1820'
						align='center'
						paddingBottom='15px'
					>
						{userInfo.is_superuser ? 'Superuser' : 'Administrator'}
					</Typography>
				}
				<Toast
					severity={toast.severity}
					message={toast.message}
					toastOpen={toast.open}
					setToastOpen={() => setToast({ ...toast, open: false })}
				/>
				<Grid container spacing={2}>
					<Grid item xs={12} md={3} lg={2} container justifyContent="center">
						<UserAvatar
							firstName={userInfo.first_name}
							lastName={userInfo.last_name}
							jobTitle={userInfo.job_title}
							company={userInfo.company}
							color='green'
						/>
					</Grid>
					<Grid item xs={12} md={9}>
						<Paper style={{ padding: 16, marginBottom: 16, border: '1px solid lightgray' }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Grid container spacing={3}>
									<Grid item xs={12}><Typography variant="h5">User Info</Typography></Grid>
									<Grid item xs={6}>
										<TextField
											label="First Name"
											value={txtFirstName}
											error={!firstNameValid}
											helperText={!firstNameValid ? 'Please enter a valid first name' : ''}
											onChange={(e) => setTxtFirstName(e.target.value)}
											size='small'
											fullWidth
											inputProps={{ maxLength: 50 }}
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											label="Last Name"
											value={txtLastName}
											error={!lastNameValid}
											helperText={!lastNameValid ? 'Please enter a valid last name' : ''}
											onChange={(e) => setTxtLastName(e.target.value)}
											size='small'
											fullWidth
											inputProps={{ maxLength: 50 }}
										/>
									</Grid>
									<Grid item xs={12}>
										<TextField
											label="Job Title"
											value={txtJobTitle}
											onChange={(e) => setTxtJobTitle(e.target.value)}
											size='small'
											fullWidth
											inputProps={{ maxLength: 64 }}
										/>
									</Grid>
									<Grid item xs={12}>
										<TextField
											label="Company Name"
											value={txtCompany}
											onChange={(e) => setTxtCompany(e.target.value)}
											size='small'
											fullWidth
											inputProps={{ maxLength: 64 }}
										/>
									</Grid>
								</Grid>
							</Box>
						</Paper>
						<Paper style={{ padding: 16, marginBottom: 16, border: '1px solid lightgray' }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Grid container spacing={3}>
									<Grid item xs={12}><Typography variant="h5">Authentication</Typography></Grid>
									<Grid item xs={12}>
										<TextField
											label="Email"
											value={txtEmail}
											error={!emailValid}
											helperText={!emailValid ? 'Please enter a valid email address' : ''}
											onChange={(e) => setTxtEmail(e.target.value)}
											size='small'
											fullWidth
											inputProps={{ maxLength: 64 }}
										/>
									</Grid>
									<Grid item xs={4} sm={3} lg={2}>
										<Box display="flex" alignItems="center" height="100%">
											<Typography fontFamily='Saira, sans-serif'>Password:</Typography>
										</Box>
									</Grid>
									<Grid item xs={8} sm={9} lg={10}>
										<Tooltip title='Reset your password'>
											<Button onClick={handlePassResetOpen} variant="contained" color="primary">Reset</Button>
										</Tooltip>
									</Grid>
									<Grid item xs={4} sm={3} lg={2}>
										<Box display="flex" alignItems="center" height="100%">
											<Typography fontFamily='Saira, sans-serif'>Multifactor Authentication:</Typography>
										</Box>
									</Grid>
									<Grid item xs={8} sm={9} lg={10}>
										{mfaSection}
									</Grid>
								</Grid>
								<PasswordResetDialog
									emailAddress={userInfo.email}
									dialogOpen={passDialogOpen}
									setDialogOpen={setPassDialogOpen}
								/>
								<MFASetupDialog
									dialogOpen={mfaSetupDialogOpen}
									setDialogOpen={setMFASetupDialogOpen}
								/>
								<DeactivateMFADialog
									dialogOpen={mfaDeactivateDialogOpen}
									setDialogOpen={setMFADeactivateDialogOpen}
								/>
								{/* <MFABackupCodesDialog
									dialogOpen={mfaBackupDialogOpen}
									setDialogOpen={setMFABackupDialogOpen}
								/> */}
							</Box>
						</Paper>
						<Paper style={{ padding: 16, marginBottom: 16, border: '1px solid lightgray' }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Grid container spacing={3}>
									<Grid item xs={12}><Typography variant="h5">Preferences</Typography></Grid>
									<Grid item xs={4} sm={3} lg={2}>
										<Box display="flex" alignItems="center" height="100%">
											<Typography fontFamily='Saira, sans-serif'>Time Zone:</Typography>
										</Box>
									</Grid>
									<Grid item xs={8} sm={9} lg={10}>
										<Tooltip title='Choose the timezone that all dates on the site will be shown in'>
											<Select
												value={txtTimeZone}
												error={!timeZoneValid}
												onChange={(e) => setTxtTimeZone(e.target.value)}
												size='small'
												style={{ width: '100%' }}
											>
												{TIME_ZONES.map((option) => (
													<MenuItem key={option} value={option}>
														{option}
													</MenuItem>
												))}
											</Select>
										</Tooltip>
									</Grid>
									<Grid item xs={4} sm={3} lg={2}>
										<Box display="flex" alignItems="center" height="100%">
											<Typography fontFamily='Saira, sans-serif'>Units:</Typography>
										</Box>
									</Grid>
									<Grid item xs={8} sm={9} lg={10}>
										<Tooltip title='Choose if you would prefer Imperial or Metric measurement units'>
											<Stack direction="row" spacing={1} alignItems="center">
												<Typography fontFamily='Saira, sans-serif'>Imperial</Typography>
													<DataSwitch checked={chkMetric} onChange={() => setChkMetric(!chkMetric)} />
												<Typography fontFamily='Saira, sans-serif'>Metric</Typography>
											</Stack>
										</Tooltip>
									</Grid>
								</Grid>
							</Box>
						</Paper>
						<AccountActivity />
						<SaveSection />
					</Grid>
				</Grid>
			</Paper>
		</>
	);
}